import IApiResponse from "../interfaces/IApiResponse";
import SuccessApiResponse from "../data/SuccessApiResponse";
import IReadingPlanRecord from "../interfaces/IReadingPlanRecord";
import { isPlatform } from "@ionic/react";
import ReadingPlanRecordServiceHybrid from "./hybrid/ReadingPlanRecordServiceHybrid";
import ReadingPlanRecordServiceWeb from "./web/ReadingPlanRecordServiceWeb";
import IReadingPlanSession from "../interfaces/IReadingPlanSession";
import ReadingPlanSessionTypes from "../data/ReadingPlanSessionTypes";
import FailedApiResponse from "../data/FailedApiResponse";
import NoActivePlanURL from "../data/NoActivePlanURL";
import {ReadingPlans, AllReadingPlanCodes, ReadingPlanCodeValues, ReadingPlanCodes, createDefaultReadingPlanRecord } from "../data/ReadingPlans";
import { BibleChapterIndex, searchBibleChapterIndex } from "../data/BibleChapterIndex";
import { BibleBookNames, BibleBookNamesType } from "../data/BibleBookNames";
import { BibleExtraVideos } from "../data/BibleExtraVideos";

export interface IReadingPlanRecordService {
    createReadingPlanRecord: (
        readingPlanRecords: IReadingPlanRecord
    ) => Promise<IApiResponse<IReadingPlanRecord>>;
    getReadingPlanRecord: (activeReadingPlanId: number) => Promise<IApiResponse<IReadingPlanRecord>>;
    getReadingPlanRecords: () => Promise<IApiResponse<IReadingPlanRecord[]>>;
    updateReadingPlanRecord: (
        readingPlanRecords: IReadingPlanRecord,
        isSyncUpdate?: boolean
    ) => Promise<IApiResponse<IReadingPlanRecord>>;

    deleteReadingPlanRecord: (recordId: number) => Promise<IApiResponse<boolean>>;
    createPlanSyncCode?: (date: Date, pace: number, plan: ReadingPlanCodeValues) => string;
    interpretSyncCode?: (
        code: string
    ) => { startDate: Date; sessionsPerDay: number; readingPlan: string; planId: number; readingPlanCode: ReadingPlanCodeValues } | void;
    nextUnreadSessionURL?: (activeReadingPlanId: number) => Promise<string>;
    scheduledSessionURL?: (activeReadingPlanId: number) => Promise<string>;
    getSession?: (planRecord: IReadingPlanRecord, sessionNumber: string) => Promise<IApiResponse<any> | undefined>;
}

const ReadingPlanRecordService: IReadingPlanRecordService = {
    createReadingPlanRecord: (readingPlanRecord) => {
        if (isPlatform("hybrid")) {
            console.log("HYBRID - ReadingPlanRecordService - Create");
            return ReadingPlanRecordServiceHybrid.createReadingPlanRecord(
                readingPlanRecord
            );
        } else {
            console.log("WEB - ReadingPlanRecordService - Create");
            return ReadingPlanRecordServiceWeb.createReadingPlanRecord(
                readingPlanRecord
            );
        }
    },

    getReadingPlanRecord: (activeReadingPlanId: number) => {
        if (isPlatform("hybrid")) {
            console.log("HYBRID - ReadingPlanRecordService - Get");
            return ReadingPlanRecordServiceHybrid.getReadingPlanRecord(activeReadingPlanId);
        } else {
            console.log("WEB - ReadingPlanRecordService - Get");
            return ReadingPlanRecordServiceWeb.getReadingPlanRecord(activeReadingPlanId);
        }
    },

    getReadingPlanRecords: async () => {
        let response: IApiResponse<IReadingPlanRecord[]> = SuccessApiResponse;
        response.Data = [];

        if (isPlatform("hybrid")) {
            console.log("HYBRID - ReadingPlanRecordService - GetAll");
            response = await ReadingPlanRecordServiceHybrid.getReadingPlanRecords()
            console.log('get plan app', response);

        } else {
            console.log("WEB - ReadingPlanRecordService - GetAll");
            response = await ReadingPlanRecordServiceWeb.getReadingPlanRecords();
            console.log('get plan web', response);
        }
        
        let noPlanBible = response.Data!.find(plan => plan.readingPlanCode === ReadingPlanCodes.bible);
        const readingPlans = response.Data!.filter(plan =>  plan.readingPlanCode !== ReadingPlanCodes.bible);                  
        
        // if noPlanBible doesn't exist create it
        if(!noPlanBible) {
            const createPlanResult = await ReadingPlanRecordService.createReadingPlanRecord(
                createDefaultReadingPlanRecord(ReadingPlanCodes.bible))
                    .catch(error => { 
                    console.error(error);
                    });
            if(createPlanResult && createPlanResult.Success) {
                noPlanBible = createPlanResult.Data!;
                response.Success = true;
                response.Message.UserMessage = 'Created no plan Bible because it did not exist';
            }
        }

        // if noPlanBible doesn't exist and creating it fails, hide this feature
        const planSet = noPlanBible ? [noPlanBible, ...readingPlans] : readingPlans;
        response.Data = planSet;
        
        console.log('Reading Plan Set', response, planSet);
        return response;
    },

    updateReadingPlanRecord: (readingPlanRecord, isSyncUpdate?) => {
        if (isPlatform("hybrid")) {
            console.log("HYBRID - ReadingPlanRecordService - Update");
            return ReadingPlanRecordServiceHybrid.updateReadingPlanRecord(
                readingPlanRecord,
                isSyncUpdate
            );
        } else {
            console.log("WEB - ReadingPlanRecordService - Update");
            return ReadingPlanRecordServiceWeb.updateReadingPlanRecord(
                readingPlanRecord,
                isSyncUpdate
            );
        }
    },

    deleteReadingPlanRecord: (recordId) => {
        if (isPlatform('hybrid')) {
            console.log('HYBRID - PrayerService')
            return ReadingPlanRecordServiceHybrid.deleteReadingPlanRecord(recordId);
        } else {
            console.log('WEB - PrayerService')
            return ReadingPlanRecordServiceWeb.deleteReadingPlanRecord(recordId);
        } 
    },

    createPlanSyncCode: (date: Date, pace: number, plan: ReadingPlanCodeValues) => {
        return `${plan}_${pace}_${date.valueOf()}`;
    },

    interpretSyncCode: (code: string) => {
        // code is plan_speed_dateInMilliseconds 'CCRBRP_1_1231923719231
        const CodeSlipArray = code.split("_");

        if (
            CodeSlipArray.length === 3 &&
            AllReadingPlanCodes.find((code: ReadingPlanCodeValues) => code === CodeSlipArray[0] as ReadingPlanCodeValues) &&
            Number.isInteger(parseInt(CodeSlipArray[1])) &&
            Number.isInteger(parseInt(CodeSlipArray[2]))
        ) {
            const readingPlanCode: ReadingPlanCodeValues = CodeSlipArray[0] as ReadingPlanCodeValues;
            const readingPlan = ReadingPlans[readingPlanCode].readingPlan;
            let sessionsPerDay = parseInt(CodeSlipArray[1]);
            const startDate = new Date(parseInt(CodeSlipArray[2]));
            const planId = ReadingPlans[readingPlanCode].id;

            return { startDate, sessionsPerDay, readingPlan, planId, readingPlanCode };
        }
    },

    nextUnreadSessionURL: (activeReadingPlanId: number) => {
        // output URL: /bible/PlanCode/Session#/Translation
        // output URL: /bible/BIBLE/1JN_1/ESV
        const sessionURL =
            ReadingPlanRecordService.getReadingPlanRecord(activeReadingPlanId).then((record) => {
                if (record.Success) {
                    const planCode = record.Data?.readingPlanCode;
                    let nextUnreadSession = 1;
                    if(planCode !== ReadingPlanCodes.bible) {
                        const completedSessions = record.Data
                            ? record.Data.completedSessions
                                .map((completed, index) => {
                                    return { index, completed };
                                })
                                .filter((session) => session.completed === true)
                            : [];
                        if (completedSessions.length) {
                            // set to the next session that is not been read (+ 2 because +1 for index correction and +1 for next day)
                            nextUnreadSession =
                                completedSessions[completedSessions?.length - 1].index + 2;
                        }
                    } else {
                        const lastSession = record.Data?.lastFreeStylePassage;
                        const reference = lastSession ? lastSession.split(' ') : ['GEN', '1'];
                        const book = reference[0] as BibleBookNamesType; 
                        const chapter = reference[1];
                        nextUnreadSession = BibleChapterIndex[book][+chapter];
                        console.log('Last Read Bible No Plan', lastSession,  book, chapter, nextUnreadSession);
                    }
                        
                        return `/bible/${planCode}/${nextUnreadSession}`;

                } else {
                    // if fail fall back to Chronological Cross Reference Bible Reading Plan Day 1
                    return NoActivePlanURL;
                }
            });
        return sessionURL;
    },
    scheduledSessionURL: (activeReadingPlanId: number) => {
        // output URL: ./bible/PlanCode/Session#/Translation
        // output URL: ./bible/BIBLE/1JN_1/ESV
        const sessionURL =
            ReadingPlanRecordService.getReadingPlanRecord(activeReadingPlanId).then((record) => {
                console.log("got record", record);
                if (record.Success) {
                    const planCode = record.Data?.readingPlanCode;
                    let scheduledSession = record.Data ? calculateScheduledDay(record.Data.startDate, new Date(Date.now())) : 1;
                    // Adjust for sessions per day
                    scheduledSession = record.Data ? (scheduledSession*record.Data.sessionsPerDay)-record.Data.sessionsPerDay+1 : scheduledSession;
                    
                    return `/bible/${planCode}/${scheduledSession}`
                } else {
                    // if fail fall back to Chronological Cross Reference Bible Reading Plan Day 1
                    return NoActivePlanURL;
                }
            });
        return sessionURL;
    },

    getSession: async (planRecord: IReadingPlanRecord, sessionNumber: string) => {
        console.log("getSession Params", planRecord, sessionNumber);
        // NOTE: if ApiResponse.Success == true then Data type will be IReadingPlanSession
        let sessionsURL: string | undefined  = '';
        
        if(!planRecord || !planRecord?.sessionsType || !sessionNumber){
            return new Promise(() => { return {...FailedApiResponse, Data: false}});
        }

        switch(planRecord.sessionsType) {
            case ReadingPlanSessionTypes.files:
                sessionsURL = `${planRecord.sessionsURL}session_${sessionNumber.padStart(4, '0')}.json`;
                break;
            case ReadingPlanSessionTypes.file:
                sessionsURL = planRecord.sessionsURL;
                break;
        }

        if(planRecord.sessionsType === ReadingPlanSessionTypes.files || planRecord.sessionsType === ReadingPlanSessionTypes.file)
        {
            console.log('SessionFile:', sessionsURL)
            const sessionResponse = await fetch(
                sessionsURL!,
                {
                    headers : { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                    }
            })
                .then((response) => {
                    return response.json();
                    })
                .then(sessionObj => {
                        console.log('fetched bible reading session: ', sessionObj);
                        const apiResponse = {...SuccessApiResponse};
                        switch (planRecord.sessionsType) {
                            case ReadingPlanSessionTypes.files:
                                apiResponse.Data = sessionObj as IReadingPlanSession
                                return apiResponse;
                            case ReadingPlanSessionTypes.file:
                                apiResponse.Data = sessionObj[sessionNumber] as IReadingPlanSession;
                                return apiResponse;
                        }
                })
                .catch(error => {
                    console.error(error);
                    return FailedApiResponse as IApiResponse<IReadingPlanSession>;
                });

            if(sessionResponse?.Success)
            {
                const sessionData = sessionResponse.Data as IReadingPlanSession;
                if(sessionData.biblePassageList.length === 1){
                    const reference = sessionData.biblePassageList[0].split(' ');
                    const book = reference[0] as BibleBookNamesType; 
                    const chapter = reference[1];

                    if(!sessionData.videos?.length && 
                        book && !Number.isNaN(chapter) &&
                        book in BibleExtraVideos &&
                        BibleExtraVideos[book]?.length > +chapter) {
                        const extraVideos = BibleExtraVideos[book][+chapter];
                        sessionData.videos = extraVideos;
                        sessionResponse.Data = sessionData;
                    }
                }
            }
            return sessionResponse;
        } else {
            return {...FailedApiResponse, Data: false};
        }
    }
};

function calculateScheduledDay(a:Date, b:Date) {
    // Discard the time and time-zone information.
    const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
    const _MS_PER_DAY = 1000 * 60 * 60 * 24;
    const daysFromStart = Math.floor((utc2 - utc1) / _MS_PER_DAY);
    return daysFromStart && daysFromStart > 0 ? (daysFromStart + 1): 1;
  }

export default ReadingPlanRecordService;
